import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL } from '../../../core/config/api-config';
import { BusinessCalendar, Department, Queue, SlaPolicy, TicketCategory } from './ticketing-config.models';

interface ActionResult {
  success: boolean;
  failureReason: string | null;
}

/** HTTP client for the five Ticketing Module lookup-entity administration controllers:
 * DepartmentsController, QueuesController, TicketCategoriesController, SlaPoliciesController,
 * BusinessCalendarsController -- grouped into one service since the Administration screen
 * presents them as tabs of one "Ticketing Configuration" page, not five separate routes. */
@Injectable({ providedIn: 'root' })
export class TicketingConfigApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  // Departments
  getDepartments(includeInactive = true): Observable<Department[]> {
    return this.http.get<Department[]>(`${this.baseUrl}/v1/departments`, { params: { includeInactive } });
  }

  createDepartment(name: string, description: string | null): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/departments`, { name, description });
  }

  updateDepartment(departmentId: string, name: string, description: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/v1/departments/${departmentId}`, { name, description });
  }

  setDepartmentActive(departmentId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/departments/${departmentId}/active`, { isActive });
  }

  // Queues
  getQueues(includeInactive = true): Observable<Queue[]> {
    return this.http.get<Queue[]>(`${this.baseUrl}/v1/queues`, { params: { includeInactive } });
  }

  createQueue(
    departmentId: string | null,
    name: string,
    description: string | null,
    priority: number,
    capacity: number | null,
    assignmentStrategyName: string | null = null,
  ): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/queues`, {
      departmentId,
      name,
      description,
      priority,
      capacity,
      assignmentStrategyName,
    });
  }

  updateQueue(
    queueId: string,
    departmentId: string | null,
    name: string,
    description: string | null,
    priority: number,
    capacity: number | null,
    assignmentStrategyName: string | null = null,
  ): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/v1/queues/${queueId}`, {
      departmentId,
      name,
      description,
      priority,
      capacity,
      assignmentStrategyName,
    });
  }

  setQueueActive(queueId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/queues/${queueId}/active`, { isActive });
  }

  setDefaultQueue(queueId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/queues/${queueId}/default`, {});
  }

  addQueueMember(queueId: string, agentUserId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/queues/${queueId}/members`, { agentUserId });
  }

  removeQueueMember(queueId: string, agentUserId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/v1/queues/${queueId}/members/${agentUserId}`);
  }

  // Ticket Categories
  getCategories(includeInactive = true): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(`${this.baseUrl}/v1/ticket-categories`, { params: { includeInactive } });
  }

  createCategory(parentCategoryId: string | null, name: string, description: string | null): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/ticket-categories`, { parentCategoryId, name, description });
  }

  updateCategory(categoryId: string, parentCategoryId: string | null, name: string, description: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/v1/ticket-categories/${categoryId}`, { parentCategoryId, name, description });
  }

  setCategoryActive(categoryId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/ticket-categories/${categoryId}/active`, { isActive });
  }

  // SLA Policies
  getSlaPolicies(includeInactive = true): Observable<SlaPolicy[]> {
    return this.http.get<SlaPolicy[]>(`${this.baseUrl}/v1/sla-policies`, { params: { includeInactive } });
  }

  createSlaPolicy(
    name: string,
    priority: string,
    responseTimeMinutes: number,
    resolutionTimeMinutes: number,
    escalationThresholdMinutes: number | null,
    businessCalendarId: string | null,
  ): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/sla-policies`, {
      name,
      priority,
      responseTimeMinutes,
      resolutionTimeMinutes,
      escalationThresholdMinutes,
      businessCalendarId,
    });
  }

  updateSlaPolicy(
    slaPolicyId: string,
    name: string,
    responseTimeMinutes: number,
    resolutionTimeMinutes: number,
    escalationThresholdMinutes: number | null,
    businessCalendarId: string | null,
  ): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/v1/sla-policies/${slaPolicyId}`, {
      name,
      responseTimeMinutes,
      resolutionTimeMinutes,
      escalationThresholdMinutes,
      businessCalendarId,
    });
  }

  setSlaPolicyActive(slaPolicyId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/sla-policies/${slaPolicyId}/active`, { isActive });
  }

  // Business Calendars
  getBusinessCalendars(includeInactive = true): Observable<BusinessCalendar[]> {
    return this.http.get<BusinessCalendar[]>(`${this.baseUrl}/v1/business-calendars`, { params: { includeInactive } });
  }

  createBusinessCalendar(name: string, timeZoneId: string): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/business-calendars`, { name, timeZoneId });
  }

  updateBusinessCalendar(businessCalendarId: string, name: string, timeZoneId: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/v1/business-calendars/${businessCalendarId}`, { name, timeZoneId });
  }

  setBusinessCalendarActive(businessCalendarId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/business-calendars/${businessCalendarId}/active`, { isActive });
  }

  addBusinessHours(businessCalendarId: string, dayOfWeek: number, startTime: string, endTime: string): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/business-calendars/${businessCalendarId}/business-hours`, {
      dayOfWeek,
      startTime,
      endTime,
    });
  }

  removeBusinessHours(businessCalendarId: string, businessHoursEntryId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/v1/business-calendars/${businessCalendarId}/business-hours/${businessHoursEntryId}`);
  }

  addHoliday(businessCalendarId: string, date: string, name: string): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${this.baseUrl}/v1/business-calendars/${businessCalendarId}/holidays`, { date, name });
  }

  removeHoliday(businessCalendarId: string, holidayId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/v1/business-calendars/${businessCalendarId}/holidays/${holidayId}`);
  }
}
